import React, { useState, useEffect, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fadeUp, fadeUpStagger, viewport } from '../utils/animations';

/* ─── Optimized Lazy Video Component ────────────────────── */
const LazyVideo = memo(function LazyVideo({ src, autoPlay, className, ...props }) {
  const videoRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasLoaded) setHasLoaded(true);
          if (autoPlay && videoRef.current) {
            videoRef.current.play().catch(() => {});
          }
        } else {
          if (autoPlay && videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      { rootMargin: '600px' }
    );
    const el = videoRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [autoPlay, hasLoaded]);

  return (
    <video
      ref={videoRef}
      src={hasLoaded ? src : undefined}
      className={className}
      autoPlay={autoPlay && hasLoaded}
      {...props}
    />
  );
});

/* ─── Social Cuts data ─────────────────────────────────── */
const socialCuts = [
  { id: 1, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/55c8cd2d9571e42de6802a1aed602c7c.mp4', type: 'video' },
  { id: 2, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/1dc878d5ef6027d6895ad4cf2d004962.mp4', type: 'video' },
  { id: 3, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/df504368a8b0f682625c4f5f6bcf5ea0.mp4', type: 'video' },
  { id: 4, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/807067b9c17359ad59f3879842892e9c.mp4', type: 'video' },
  { id: 5, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/5e0415d039f9c6f166b2e6498a8f34ad.mp4', type: 'video' },
  { id: 6, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/2d601cecc57d156a96f89646aacea094.mp4', type: 'video' },
  { id: 7, src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/a1cbb65e81274d01e207b22761338aae.mp4', type: 'video' },
];

/* ─── Marquee CSS ───────────────────────────────────────── */
const MARQUEE_STYLE = `
@keyframes marquee-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.marquee-track {
  display: flex;
  width: max-content;
  animation: marquee-scroll 55s linear infinite;
}
.marquee-wrap:hover .marquee-track {
  animation-play-state: paused;
}
`;

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home',     item: 'https://bayareapodcast.studio/' },
    { '@type': 'ListItem', position: 2, name: 'Our Work', item: 'https://bayareapodcast.studio/portfolio' },
  ],
};

/* ─── Single vertical card ──────────────────────────────── */
function SocialCard({ item, onClick }) {
  return (
    <div
      className="relative flex-shrink-0 w-52 md:w-60 aspect-[9/16] bg-neutral-900 border border-white/10 overflow-hidden cursor-pointer group transition-transform duration-300 hover:scale-[1.04] hover:z-10"
      style={{ marginRight: '16px' }}
      onClick={() => onClick(item)}
    >
      <LazyVideo
        src={item.src}
        autoPlay loop muted playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  );
}

/* ─── Vertical lightbox ─────────────────────────────────── */
function VerticalLightbox({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      onClick={onClose}
    >
      <div
        className="relative flex flex-col"
        style={{ width: 'min(340px, 88vw)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={onClose}
            className="font-mono text-xs text-white/50 hover:text-white transition-colors px-2 py-1 border border-white/10 hover:border-white/30 leading-none"
            aria-label="Close"
          >
            [ × ]
          </button>
        </div>
        <div className="relative w-full aspect-[9/16] bg-black border border-white/10 overflow-hidden scrollbar-hide">
          <video
            src={item.src}
            autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────── */
export default function OurWork() {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [activeSocialCut, setActiveSocialCut] = useState(null);

  // ── marquee style injection ──────────────────────────────────────────────
  useEffect(() => {
    const id = 'sc-marquee-style';
    if (!document.getElementById(id)) {
      const tag = document.createElement('style');
      tag.id = id;
      tag.textContent = MARQUEE_STYLE;
      document.head.appendChild(tag);
    }
  }, []);

  // ── title ────────────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = 'Portfolio | Bay Area Podcast Studio';
    return () => {
      document.title = 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio';
    };
  }, []);

  // ── meta tags ────────────────────────────────────────────────────────────
  useEffect(() => {
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.match(/\[([^=]+)="([^"]+)"\]/).slice(1);
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const defaults = {
      desc:    document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
      ogDesc:  document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
      ogUrl:   document.querySelector('meta[property="og:url"]')?.getAttribute('content') || '',
      twTitle: document.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || '',
      twDesc:  document.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || '',
      keywords:document.querySelector('meta[name="keywords"]')?.getAttribute('content') || '',
    };

    setMeta('meta[name="description"]',        'content', 'Explore the video production portfolio from Bay Area Podcast Studio. Featuring published shows, executive podcasts, and video sessions for Marcin Kleczynski of Malwarebytes, Kari Byron of Mythbusters, Azita Martin of Nvidia, Jason Conyard of VMware, Sylvia Acevedo of Girl Scouts, Aarthi Kumar of Microsoft, Mustafa Furniturewala of Coursera, Amar Amte of Pegbo, Nataliya Scarberry of Contenu, and other Silicon Valley leaders.');
    setMeta('meta[name="keywords"]',           'content', 'video production portfolio Bay Area, Marcin Kleczynski Malwarebytes video, Kari Byron Mythbusters podcast, Azita Martin Nvidia video, Jason Conyard VMware interview, Sylvia Acevedo Girl Scouts video, Aarthi Kumar Microsoft video, Mustafa Furniturewala Coursera interview, Amar Amte Pegbo video, Nataliya Scarberry Contenu video, Glynn Washington Snap Judgment, Adam Rogers Alien Earth Podcast, Hetal Vasavada chef video, Alex Rechevskiy Google PM video, Jen Pelka Une Femme video, Bala Subramaniam Bites video, Anthony McCusker Goodwin Law video, Cisco Bass Fremont Bank video, Alli Moses Fremont Bank video, Bay Area video podcast studio, Silicon Valley video production portfolio, Fremont CA video studio');
    setMeta('meta[property="og:title"]',       'content', 'Portfolio | Bay Area Podcast Studio');
    setMeta('meta[property="og:description"]', 'content', 'Video production portfolio from Bay Area Podcast Studio in Fremont, CA featuring top Silicon Valley executives and founders.');
    setMeta('meta[property="og:url"]',         'content', 'https://bayareapodcast.studio/portfolio');
    setMeta('meta[name="twitter:title"]',      'content', 'Portfolio | Bay Area Podcast Studio');
    setMeta('meta[name="twitter:description"]','content', 'Video production portfolio from Bay Area Podcast Studio in Fremont, CA featuring top Silicon Valley executives and founders.');

    return () => {
      setMeta('meta[name="description"]',        'content', defaults.desc);
      setMeta('meta[name="keywords"]',           'content', defaults.keywords);
      setMeta('meta[property="og:title"]',       'content', defaults.ogTitle);
      setMeta('meta[property="og:description"]', 'content', defaults.ogDesc);
      setMeta('meta[property="og:url"]',         'content', defaults.ogUrl);
      setMeta('meta[name="twitter:title"]',      'content', defaults.twTitle);
      setMeta('meta[name="twitter:description"]','content', defaults.twDesc);
    };
  }, []);

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────
  useEffect(() => {
    const id = 'ld-ourwork-breadcrumb';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(BREADCRUMB_JSON_LD);
    return () => {
      const tag = document.getElementById(id);
      if (tag) tag.remove();
    };
  }, []);

  // ── body scroll lock ─────────────────────────────────────────────────────
  useEffect(() => {
    const isOpen = selectedMedia || activeSocialCut;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedMedia, activeSocialCut]);

  // ── esc key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleEscKey = (e) => { if (e.key === 'Escape') setSelectedMedia(null); };
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, []);

  const youtubeEmbeds = [
    { id: 1, link: "https://youtu.be/evNobJoRqXI", thumbnail: "https://img.youtube.com/vi/evNobJoRqXI/maxresdefault.jpg", alt: "Published video podcast episode with Silicon Valley executive produced at Bay Area Podcast Studio Fremont CA" },
    { id: 2, link: "https://youtu.be/UMtoHdweSGU", thumbnail: "https://img.youtube.com/vi/UMtoHdweSGU/maxresdefault.jpg", alt: "Executive interview video podcast session produced at Bay Area Podcast Studio" },
    { id: 3, link: "https://youtu.be/NNFx2yXgypI", thumbnail: "https://img.youtube.com/vi/NNFx2yXgypI/maxresdefault.jpg", alt: "Corporate video podcast session produced at Bay Area Podcast Studio in Fremont CA" },
    { id: 4, link: "https://youtu.be/pViV_S8IwKI", thumbnail: "https://img.youtube.com/vi/pViV_S8IwKI/maxresdefault.jpg", alt: "Video podcast episode produced at Bay Area Podcast Studio for Silicon Valley technology clients" },
    { id: 5, link: "https://youtu.be/XoMGZkbaCxI", thumbnail: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/dceed594c0d967f25cfee9650b7820e3.png", alt: "Published founder interview show produced at Bay Area Podcast Studio Fremont CA" },
    { id: 6, link: "https://youtu.be/ZWis4ZJoLIE", thumbnail: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/e20b83dfe33028da9dd85ef951ab2926.png", alt: "High-end 4K video podcast broadcast produced at Bay Area Podcast Studio" },
    { id: 7, link: "https://youtu.be/33iw4vEBTuk", thumbnail: "https://img.youtube.com/vi/33iw4vEBTuk/maxresdefault.jpg", alt: "Video production session for Bay Area startup founder at Bay Area Podcast Studio" },
    { id: 8, link: "https://youtu.be/4aZQOoC6u_c", thumbnail: "https://img.youtube.com/vi/4aZQOoC6u_c/maxresdefault.jpg", alt: "On-location corporate video podcast episode produced at Bay Area Podcast Studio" },
    { id: 9, link: "https://youtu.be/MWrisMyobBE", thumbnail: "https://img.youtube.com/vi/MWrisMyobBE/maxresdefault.jpg", alt: "4K multi-camera video podcast session at Bay Area Podcast Studio Fremont CA" },
    { id: 10, link: "https://youtu.be/sl233dH0JgE", thumbnail: "https://img.youtube.com/vi/sl233dH0JgE/maxresdefault.jpg", alt: "Published video podcast episode with industry expert produced at Bay Area Podcast Studio" }
  ];

  const editorialTags = [
    { id: 1, text: "MARCIN KLECZYNSKI / MALWAREBYTES", alt: "Executive video podcast session with Marcin Kleczynski, CEO of Malwarebytes — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/4196a3e2f41d1baedf2e7e87f0d87309.webp" },
    { id: 2, text: "KARI BYRON / MYTHBUSTERS", alt: "Talent session with Kari Byron from Mythbusters — Bay Area Podcast Studio Fremont CA", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/d5cbe871c85543c4ec27717d6a4fd1a0.mov" },
    { id: 3, text: "JASON CONYARD / VMWARE", alt: "Executive interview with Jason Conyard of VMware — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/4b9cb2868e781db7b400d6f13ccf6b29.png" },
    { id: 4, text: "AZITA MARTIN / NVIDIA", alt: "Brand video session with Azita Martin, VP of Marketing at Nvidia — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/ef2c4958151430faa4e76874daf93a23.mov" },
    { id: 5, text: "CISCO BASS / FREMONT BANK", alt: "Brand interview with Cisco Bass of Fremont Bank — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvanBlZw==/21d8d9a77dfd556c1fbec96d3c03c6c4.jpg" },
    { id: 6, text: "ANTHONY MCCUSKER / GOODWIN LAW", alt: "Corporate interview with Anthony McCusker of Goodwin Law — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/f1fa3f1456622631584e5a9e18cae964.webp" },
    { id: 7, text: "SYLVIA ACEVEDO / GIRL SCOUTS", alt: "Leadership video session with Sylvia Acevedo, former CEO of Girl Scouts — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/9f283df465a7a2d505badb638d30b7c3.png" },
    { id: 8, text: "AARTHI KUMAR / MICROSOFT", alt: "Executive media session with Aarthi Kumar of Microsoft — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvanBlZw==/5d7c7cabba089eda53a2ce305dc25cfe.jpg" },
    { id: 9, text: "GLYNN WASHINGTON / SNAP JUDGMENT", alt: "Media interview with Glynn Washington, Host of Snap Judgment — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/e14830f5695c990b64e5b470d302fcd2.mov" },
    { id: 10, text: "ADAM ROGERS / ALIEN EARTH PODCAST", alt: "Video podcast session with Adam Rogers, Host of Alien Earth Podcast — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/86e5cb0b48e200496437af978a68dd64.mov" },
    { id: 11, text: "HETAL VASAVADA / CHEF", alt: "Video production session with Hetal Vasavada, Chef and Author — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/15d91c2b0ecdcc1ed94eaf373d0a2064.webp" },
    { id: 12, text: "NATALIYA SCARBERRY / CONTENU", alt: "Content creator video session with Nataliya Scarberry, Founder of Contenu — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/04080691f08fd5b7d76036c5e0b6d7ec.mov" },
    { id: 13, text: "MUSTAFA FURNITUREWALA / COURSERA", alt: "Executive session with Mustafa Furniturewala, CTO of Coursera — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/1011a1f75c25b08abaae122e85f72566.png" },
    { id: 14, text: "ALEX RECHEVSKIY / GOOGLE PM", alt: "Video podcast session with Alex Rechevskiy, Google Product Manager — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/7995b15461f89ac7ca4c61dd853bda64.mov" },
    { id: 15, text: "ALLI MOSES / FREMONT BANK", alt: "Corporate video session with Alli Moses of Fremont Bank — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/03240d2e97024fec2651c6bed37a15db.png" },
    { id: 16, text: "AMAR AMTE / PEGBO", alt: "Founder video session with Amar Amte, Founder of Pegbo — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/f6ddc4700188cabe933d10db02bd96d2.mov" },
    { id: 17, text: "BALA SUBRAMANIAM / BITES", alt: "Startup founder video session with Bala Subramaniam of Bites — Bay Area Podcast Studio", type: "CINEMATIC STILL", image: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/76ddfc9ac34c388fad305364c5f1dfbe.png" },
    { id: 18, text: "JEN PELKA / UNE FEMME", alt: "Founder video session with Jen Pelka, Founder of Une Femme — Bay Area Podcast Studio", type: "SILENT VIDEO LOOP", video: "https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/55d5b1e265a9d71fc9464f0f274dbbee.mov" }
  ];

  const marqueeItems = [...socialCuts, ...socialCuts];

  return (
    <main className="bg-[#FFFFFF] text-[#000000] min-h-screen relative overflow-x-hidden scrollbar-hide">

      {/* SECTION 1: HERO BLOCK */}
      <section className="pt-20 md:pt-28 pb-10 md:pb-14 px-5 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="mb-8 md:mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase text-black mb-3 tracking-tight leading-tight">
              PRODUCTION ARCHIVE
            </h1>
            <p className="text-base md:text-xl text-neutral-900 font-medium">Real clients. Real work.</p>
          </motion.div>
          <motion.div
            className="aspect-video w-full bg-black border border-neutral-900 shadow-2xl overflow-hidden scrollbar-hide"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <LazyVideo
              src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/1aaf585ebf2f76dd77c58f5c5fd86466.mov"
              autoPlay loop muted playsInline
              className="w-full h-full object-cover object-center"
            />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: PUBLISHED WORK */}
      <section className="max-w-7xl mx-auto px-5 md:px-12 py-12 md:py-16">
        <motion.h2
          className="text-xs sm:text-sm font-mono font-bold uppercase tracking-widest text-black mb-6 md:mb-8 border-b border-neutral-200 pb-3"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
        >
          PUBLISHED WORK
        </motion.h2>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUpStagger}
        >
          {youtubeEmbeds.map((embed) => (
            <motion.div key={`youtube-${embed.id}`} variants={fadeUp}>
              {embed.link ? (
                <a
                  href={embed.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-video w-full bg-neutral-900 border border-neutral-200 shadow-md overflow-hidden relative group block"
                >
                  <img
                    src={embed.thumbnail}
                    alt={embed.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="bg-black/80 backdrop-blur-sm text-white p-4 rounded-full border border-white/20 shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={FiIcons.FiPlay} className="text-2xl ml-1" />
                    </div>
                  </div>
                </a>
              ) : (
                <div className="aspect-video w-full bg-neutral-900 border border-neutral-200 shadow-md overflow-hidden relative group flex flex-col items-center justify-center">
                  <SafeIcon icon={FiIcons.FiPlay} className="text-4xl text-neutral-600 mb-3" />
                  <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                    YOUTUBE EMBED PLACEHOLDER {embed.id}
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
        <motion.div
          className="flex justify-end w-full mt-2"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
        >
          <a
            href="https://youtube.com/playlist?list=PL0_HHKds9UTWMoP21DNPs4Tr46pGlo-rM&si=rUVmSP1b_xgwwHRj"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-xs md:text-sm font-mono font-bold uppercase tracking-wider text-black hover:text-neutral-600 transition-colors py-5 px-2 underline underline-offset-8 decoration-neutral-300 hover:decoration-black"
          >
            VIEW FULL PLAYLIST →
          </a>
        </motion.div>
      </section>

      {/* ── SECTION: SOCIAL CUTS ─────────────────────────────── */}
      <section className="py-16 md:py-24 border-t border-b border-neutral-200 bg-[#0a0a0a] overflow-hidden">
        <motion.div
          className="max-w-7xl mx-auto px-5 md:px-12 mb-10 md:mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
        >
          <h2 className="text-xs sm:text-sm font-mono font-bold uppercase tracking-widest text-white pb-3 border-b border-white/10">
            SOCIAL CUTS
          </h2>
        </motion.div>
        <div className="marquee-wrap w-full overflow-hidden scrollbar-hide">
          <div className="marquee-track py-2">
            {marqueeItems.map((item, idx) => (
              <SocialCard
                key={`sc-${item.id}-${idx}`}
                item={item}
                onClick={setActiveSocialCut}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: SELECTED FRAMES */}
      <section className="max-w-7xl mx-auto px-5 md:px-12 py-12 md:py-16">
        <motion.h2
          className="text-xs sm:text-sm font-mono font-bold uppercase tracking-widest text-black mb-6 md:mb-8 border-b border-neutral-200 pb-3"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
        >
          SELECTED FRAMES
        </motion.h2>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUpStagger}
        >
          {editorialTags.map((item) => (
            <motion.div
              key={`editorial-${item.id}`}
              variants={fadeUp}
              className="aspect-[4/3] w-full bg-neutral-100 border border-neutral-200 shadow-sm overflow-hidden relative group cursor-pointer hover:border-neutral-400 transition-colors scrollbar-hide"
              onClick={() => setSelectedMedia(item)}
            >
              {item.video ? (
                <LazyVideo
                  src={item.video}
                  autoPlay loop muted playsInline
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
                />
              ) : item.image ? (
                <img
                  src={item.image}
                  alt={item.alt}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiImage} className="text-3xl text-neutral-300" />
                </div>
              )}
              <div className="absolute bottom-3 left-3 right-3 z-10">
                <span className="inline-block bg-black text-white text-xs font-mono font-bold uppercase tracking-widest px-3 py-1.5 border border-neutral-800 shadow-lg truncate max-w-full">
                  {item.text}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* SECTION 4: CLOSING CTA */}
      <motion.section
        className="max-w-7xl mx-auto px-5 md:px-12 py-16 md:py-24 border-t border-neutral-200 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={fadeUp}
      >
        <h2 className="text-2xl sm:text-3xl md:text-5xl font-black uppercase tracking-tight text-black mb-3 leading-tight">
          READY TO BUILD?
        </h2>
        <p className="text-base md:text-xl text-neutral-800 max-w-xl mx-auto mb-8 md:mb-10 font-medium">
          Book the studio space or partner with our team on your next video production.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/studio-sessions"
            className="bg-black text-white hover:bg-neutral-800 text-sm font-bold uppercase tracking-wider px-8 py-4 border border-black transition-all w-full sm:w-auto text-center"
          >
            BOOK THE STUDIO
          </Link>
          <Link
            to="/video-services#contact"
            className="bg-transparent text-black hover:bg-neutral-100 text-sm font-bold uppercase tracking-wider px-8 py-4 border border-black transition-all w-full sm:w-auto text-center"
          >
            START A PROJECT
          </Link>
        </div>
      </motion.section>

      {/* SELECTED FRAMES LIGHTBOX */}
      {selectedMedia && (
        <div
          className="fixed inset-0 z-[999] flex flex-col items-center justify-center p-4 md:p-8"
          style={{ backgroundColor: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="fixed top-4 right-4 z-[1000] w-14 h-14 flex items-center justify-center bg-black/60 border border-white/20 text-white hover:text-neutral-300 hover:bg-black/80 transition-colors"
            onClick={(e) => { e.stopPropagation(); setSelectedMedia(null); }}
            aria-label="Close lightbox"
          >
            <SafeIcon icon={FiIcons.FiX} className="text-3xl" />
          </button>
          <div className="relative flex flex-col items-center justify-center w-full" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.video ? (
              <video
                src={selectedMedia.video}
                autoPlay loop muted playsInline
                className="max-h-[80vh] max-w-[95vw] w-auto object-contain border border-neutral-800 shadow-2xl"
              />
            ) : selectedMedia.image ? (
              <img
                src={selectedMedia.image}
                alt={selectedMedia.alt}
                className="max-h-[80vh] max-w-[95vw] w-auto object-contain border border-neutral-800 shadow-2xl"
              />
            ) : (
              <div className="h-[50vh] w-[90vw] md:w-[60vw] bg-neutral-900 border border-neutral-800 shadow-2xl flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiImage} className="text-6xl text-neutral-700" />
              </div>
            )}
            <p className="mt-4 text-center font-mono text-xs md:text-sm font-bold uppercase tracking-widest text-neutral-300 max-w-[90vw] truncate">
              {selectedMedia.text}
            </p>
          </div>
        </div>
      )}

      {/* SOCIAL CUTS VERTICAL LIGHTBOX */}
      {activeSocialCut && (
        <VerticalLightbox item={activeSocialCut} onClose={() => setActiveSocialCut(null)} />
      )}

    </main>
  );
}